/**
 * searchHash.ts — client-side PII hashing for lead search (Q5).
 *
 * The Leads panel NEVER sends a raw phone or raw CPF to the backend. The
 * operator's input is hashed IN THE BROWSER and only the hash (or a lead id)
 * leaves the page, matching the indexed `phone_sha256` / `cpf_hash` columns the
 * query API filters on (spec §Search, plan Q5).
 *
 * Reproducibility is the whole point: the hash produced here MUST equal the
 * value stored at write time, or search silently never matches. Verified
 * against the backend hashers (no salt / no server secret is involved):
 *
 *   - CPF  → `packages/shared/crypto/cpf.py::hash_cpf`:
 *       hashlib.sha256(re.sub(r"[.\-\s]", "", cpf)).hexdigest()
 *     i.e. strip dots/dashes/whitespace, then plain SHA-256 hex.
 *
 *   - phone → `packages/shared/looker_lead_puller/puller.py::_sha256_hex`:
 *       hashlib.sha256(phone_e164.encode("utf-8")).hexdigest()
 *     where `phone_e164` is the E.164 string WITH its leading "+", e.g.
 *     "+5511919193616" (migration 0002 documents the same). So the operator's
 *     input is normalised to E.164 (digits, single leading "+") before hashing.
 *
 * Both are unsalted SHA-256, so `crypto.subtle.digest('SHA-256', …)` reproduces
 * them exactly (unit-pinned in searchHash.test.ts against Python-computed
 * digests). No new client-side PII logging (Q3): nothing here logs the input.
 */

import type { LeadSearchKind, LeadSearchTerm } from '../../api/types';

/** A UUID v1–v5 shape (lead id passes through un-hashed). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isLeadIdLike = (value: string): boolean => UUID_RE.test(value.trim());

/** SHA-256 hex digest of a UTF-8 string, via Web Crypto (no salt). */
export const sha256Hex = async (input: string): Promise<string> => {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Normalise a CPF exactly as the backend does before hashing: strip dots,
 * dashes and whitespace, leaving the bare digits. Mirrors the Python
 * `re.sub(r"[.\-\s]", "", cpf)` character-for-character.
 */
export const normalizeCpf = (raw: string): string => raw.replace(/[.\-\s]/g, '');

/**
 * Normalise an operator-typed phone to the E.164 form the backend hashed:
 * keep digits only and prefix a single "+", e.g. "+55 11 91919-3616" →
 * "+5511919193616". The operator must enter full international format
 * (country code included) for the hash to match the stored value.
 */
export const normalizePhoneE164 = (raw: string): string => {
  const digits = raw.replace(/[^\d]/g, '');
  return digits.length > 0 ? `+${digits}` : '';
};

/**
 * Build the tagged, already-hashed search term sent on the wire. Raw PII is
 * consumed here and never returned: phone/CPF become a hash; a lead id passes
 * through verbatim. Returns null for an empty/blank input.
 */
export const buildSearchTerm = async (
  kind: LeadSearchKind,
  rawValue: string,
): Promise<LeadSearchTerm | null> => {
  const trimmed = rawValue.trim();
  if (trimmed === '') return null;

  switch (kind) {
    case 'lead_id':
      return { kind: 'lead_id', value: trimmed };
    case 'phone_hash': {
      const e164 = normalizePhoneE164(trimmed);
      if (e164 === '') return null;
      return { kind: 'phone_hash', value: await sha256Hex(e164) };
    }
    case 'cpf_hash': {
      const digits = normalizeCpf(trimmed);
      if (digits === '') return null;
      return { kind: 'cpf_hash', value: await sha256Hex(digits) };
    }
    default: {
      // Exhaustiveness guard: a new kind must be handled explicitly.
      const exhaustive: never = kind;
      return exhaustive;
    }
  }
};
